import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Mail,
  Shield,
  Search,
  Edit,
  Trash2,
  UserPlus,
  Phone,
  Building2,
  Briefcase,
  X,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { teamService } from '../../../services/teamService';
import { branchService } from '../../../services/branchService';
import { useDateFormat } from '../../../hooks/useDateFormat';
import UsageMeter from '../../../components/UsageMeter';
import OverageConsentModal from '../../../components/OverageConsentModal';
import { subscriptionService } from '../../../services/subscriptionService';

const Team = () => {
  const { t } = useTranslation();
  const [teamMembers, setTeamMembers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const { formatRelativeTime } = useDateFormat();
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'TECHNICIAN',
    employeeId: '',
    department: '',
    jobTitle: '',
    team: '',
    assignedBranchIds: []
  });
  const [cmmsSubscription, setCmmsSubscription] = useState(null);
  const [showOverageModal, setShowOverageModal] = useState(false);

  // Role definitions
  const roles = [
    { value: 'CMMS_ADMIN', label: t('cmms.team_roleAdmin'), color: 'bg-purple-100 text-purple-800 border-purple-200', description: t('cmms.team_roleAdminDesc') },
    { value: 'MANAGER', label: t('cmms.team_roleManager'), color: 'bg-blue-100 text-blue-800 border-blue-200', description: t('cmms.team_roleManagerDesc') },
    { value: 'SUPERVISOR', label: t('cmms.team_roleSupervisor'), color: 'bg-indigo-100 text-indigo-800 border-indigo-200', description: t('cmms.team_roleSupervisorDesc') },
    { value: 'TECHNICIAN', label: t('cmms.team_roleTechnician'), color: 'bg-green-100 text-green-800 border-green-200', description: t('cmms.team_roleTechnicianDesc') },
    { value: 'VIEWER', label: t('cmms.team_roleViewer'), color: 'bg-gray-100 text-gray-800 border-gray-200', description: t('cmms.team_roleViewerDesc') }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    subscriptionService.getCmmsSubscription()
      .then(data => setCmmsSubscription(data))
      .catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [membersData, branchesData] = await Promise.all([
        teamService.getTeamMembers(),
        branchService.getBranches()
      ]);
      setTeamMembers(membersData || []);
      setBranches(branchesData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setTeamMembers([]);
      setBranches([]);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = roles.find(r => r.value === role);
    return roleConfig || roles.find(r => r.value === 'VIEWER');
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch =
      (member.firstName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (member.lastName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (member.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (member.department || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || member.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const handleCreate = () => {
    setEditingMember(null);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      role: 'TECHNICIAN',
      employeeId: '',
      department: '',
      jobTitle: '',
      team: '',
      assignedBranchIds: []
    });
    const current = teamMembers.length;
    const max = cmmsSubscription?.includedUsers ?? null;
    if (max !== null && current >= max) {
      setShowOverageModal(true);
    } else {
      setShowModal(true);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      email: member.email,
      password: '',
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      phone: member.phone || '',
      role: member.role,
      employeeId: member.employeeId || '',
      department: member.department || '',
      jobTitle: member.jobTitle || '',
      team: member.team || '',
      assignedBranchIds: member.assignedBranchIds || []
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMember) {
        await teamService.updateMember(editingMember.id, formData);
      } else {
        await teamService.createMember(formData);
        subscriptionService.getCmmsSubscription()
          .then(data => setCmmsSubscription(data))
          .catch(() => {});
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Failed to save team member. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('cmms.team_removeConfirm'))) {
      try {
        await teamService.deleteMember(id);
        loadData();
      } catch (error) {
        console.error('Error deleting team member:', error);
        alert('Failed to delete team member. Please try again.');
      }
    }
  };

  const handleToggleStatus = async (member) => {
    try {
      await teamService.updateMemberStatus(member.id, !member.isActive);
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  // Stats
  const stats = {
    total: teamMembers.length,
    active: teamMembers.filter(m => m.isActive).length,
    technicians: teamMembers.filter(m => m.role === 'TECHNICIAN').length,
    admins: teamMembers.filter(m => m.role === 'CMMS_ADMIN' || m.role === 'MANAGER').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8" />
            {t('cmms.team_title')}
          </h1>
          <p className="text-gray-600 mt-1">{t('cmms.team_subtitle')}</p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <UserPlus className="w-5 h-5 mr-2" />
          {t('cmms.team_addMember')}
        </button>
      </div>

      {cmmsSubscription && (
        <UsageMeter
          label={t('cmms.team_users')}
          current={teamMembers.length}
          max={cmmsSubscription.includedUsers ?? null}
          className="mb-4"
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">{t('cmms.team_totalMembers')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.active}</div>
              <div className="text-sm text-gray-600">{t('common.active')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Briefcase className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.technicians}</div>
              <div className="text-sm text-gray-600">{t('cmms.team_technicians')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.admins}</div>
              <div className="text-sm text-gray-600">{t('cmms.team_adminsManagers')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('cmms.team_allRoles')}</option>
            {roles.map(role => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Team Members Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('common.loading')}</p>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('cmms.team_noMembersFound')}</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || filterRole !== 'all' ? t('cmms.team_noResults') : t('cmms.team_emptyState')}
            </p>
            {!searchQuery && filterRole === 'all' && (
              <button
                onClick={handleCreate}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                {t('cmms.team_addMember')}
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cmms.team_role')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cmms.team_department')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cmms.team_lastActive')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredMembers.map((member) => {
                  const badge = getRoleBadge(member.role);
                  return (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                            {(member.firstName || 'U')[0]}{(member.lastName || '')[0] || ''}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {member.firstName || ''} {member.lastName || ''}
                              {!member.firstName && !member.lastName && member.email}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {member.email}
                            </div>
                            {member.phone && (
                              <div className="text-sm text-gray-400 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {member.phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
                          <Shield className="w-3 h-3" />
                          {badge.label}
                        </span>
                        {member.jobTitle && (
                          <div className="text-xs text-gray-500 mt-1">{member.jobTitle}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{member.department || '-'}</div>
                        {member.team && (
                          <div className="text-xs text-gray-500">Team: {member.team}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleStatus(member)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                            member.isActive
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {member.isActive ? (
                            <>
                              <CheckCircle className="w-3 h-3" />
                              {t('common.active')}
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3 h-3" />
                              {t('common.inactive')}
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatRelativeTime(member.lastLogin)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(member)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(member.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Member Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingMember ? t('cmms.team_editMember') : t('cmms.team_addMember')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Email & Password */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.email')} *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder={t('cmms.team_phEmail')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={editingMember}
                  />
                </div>
                {!editingMember && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('common.password')} *
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="••••••••"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required={!editingMember}
                      minLength={6}
                    />
                  </div>
                )}
              </div>

              {/* Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.firstName')}
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    placeholder={t('cmms.team_phFirstName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.lastName')}
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    placeholder={t('cmms.team_phLastName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Phone & Role */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.phone')}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder={t('cmms.team_phPhone')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('cmms.team_role')} *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label} - {role.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Employment Info */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {t('cmms.team_employmentInfo')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('cmms.team_employeeId')}
                    </label>
                    <input
                      type="text"
                      value={formData.employeeId}
                      onChange={(e) => setFormData({...formData, employeeId: e.target.value})}
                      placeholder={t('cmms.team_phEmployeeId')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('cmms.team_department')}
                    </label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({...formData, department: e.target.value})}
                      placeholder={t('cmms.team_phDepartment')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('cmms.team_jobTitle')}
                    </label>
                    <input
                      type="text"
                      value={formData.jobTitle}
                      onChange={(e) => setFormData({...formData, jobTitle: e.target.value})}
                      placeholder={t('cmms.team_phJobTitle')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('cmms.team_title')}
                    </label>
                    <input
                      type="text"
                      value={formData.team}
                      onChange={(e) => setFormData({...formData, team: e.target.value})}
                      placeholder={t('cmms.team_phTeam')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Assigned Branches */}
              {branches.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('cmms.team_assignedBranches')}
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {branches.map(branch => (
                      <label key={branch.id} className="flex items-center gap-2 py-1">
                        <input
                          type="checkbox"
                          checked={(formData.assignedBranchIds || []).includes(branch.id)}
                          onChange={(e) => {
                            const ids = formData.assignedBranchIds || [];
                            if (e.target.checked) {
                              setFormData({...formData, assignedBranchIds: [...ids, branch.id]});
                            } else {
                              setFormData({...formData, assignedBranchIds: ids.filter(id => id !== branch.id)});
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{branch.name} ({branch.code})</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingMember ? t('cmms.team_updateMember') : t('cmms.team_addMember')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <OverageConsentModal
        isOpen={showOverageModal}
        onClose={() => setShowOverageModal(false)}
        resourceLabel="user"
        current={teamMembers.length}
        max={cmmsSubscription?.includedUsers ?? 0}
      >
        <div className="space-y-4">
          {/* Email & Password */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.email')} *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="member@company.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.password')} *
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                minLength={6}
              />
            </div>
          </div>

          {/* Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.firstName')}
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                placeholder="John"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.lastName')}
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                placeholder="Doe"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Phone & Role */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.phone')}
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+1 (555) 000-0000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('cmms.team_role')} *
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {roles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label} - {role.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Employment Info */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {t('cmms.team_employmentInfo')}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cmms.team_employeeId')}
                </label>
                <input
                  type="text"
                  value={formData.employeeId}
                  onChange={(e) => setFormData({...formData, employeeId: e.target.value})}
                  placeholder="EMP-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cmms.team_department')}
                </label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  placeholder="e.g., Maintenance"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cmms.team_jobTitle')}
                </label>
                <input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({...formData, jobTitle: e.target.value})}
                  placeholder="e.g., Senior Technician"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cmms.team_title')}
                </label>
                <input
                  type="text"
                  value={formData.team}
                  onChange={(e) => setFormData({...formData, team: e.target.value})}
                  placeholder="e.g., Night Shift"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Assigned Branches */}
          {branches.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('cmms.team_assignedBranches')}
              </label>
              <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                {branches.map(branch => (
                  <label key={branch.id} className="flex items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      checked={(formData.assignedBranchIds || []).includes(branch.id)}
                      onChange={(e) => {
                        const ids = formData.assignedBranchIds || [];
                        if (e.target.checked) {
                          setFormData({...formData, assignedBranchIds: [...ids, branch.id]});
                        } else {
                          setFormData({...formData, assignedBranchIds: ids.filter(id => id !== branch.id)});
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">{branch.name} ({branch.code})</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </OverageConsentModal>
    </div>
  );
};

export default Team;
